import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import AdminDashboard from "../AdminDashboard/admindash";
import {
  Hotel,
  MapPin,
  Phone,
  DollarSign,
  FileText,
  List,
  Clock,
  ImageIcon,
  Plus,
  Trash2,
  Eye,
  Loader2,
  Navigation,
  Key,
  ArrowLeft,
  Save
} from "lucide-react";
import { toast } from "react-toastify";
import API_URL from "../../config";


function UpdateAccommodation() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchAccommodation = async () => {
      try {
        const res = await axios.get(`${API_URL}/accommodation/${id}`);
        const data = res.data.accommodation || res.data;

        setFormData({
          accommodationId: data.accommodationId || "",
          accommodationName: data.accommodationName || "",
          type: data.type || "",
          description: data.description || "",
          location: data.location || "",
          address: data.address || "",
          phone: data.phone || "",
          roomType: Array.isArray(data.roomType) ? data.roomType.join(", ") : "",
          pricePerNight: data.pricePerNight || "",
          currency: data.currency || "LKR",
          availability: data.availability ?? true,
          amenities: Array.isArray(data.amenities) ? data.amenities.join(", ") : "",
          checkInTime: data.checkInTime || "",
          checkOutTime: data.checkOutTime || "",
          images: Array.isArray(data.images) && data.images.length > 0 ? data.images : [""],
        });
      } catch (err) {
        console.error("Error fetching accommodation:", err);
        toast.error("Failed to load property data.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchAccommodation();
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleImageChange = (index, value) => {
    const updatedImages = [...formData.images];
    updatedImages[index] = value;
    setFormData({ ...formData, images: updatedImages });
  };

  const addImageField = () => {
    setFormData({ ...formData, images: [...formData.images, ""] });
  };

  const removeImageField = (index) => {
    const updatedImages = formData.images.filter((_, i) => i !== index);
    setFormData({ ...formData, images: updatedImages });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const payload = {
        ...formData,
        roomType: formData.roomType ? formData.roomType.split(",").map((s) => s.trim()).filter(s => s !== "") : [],
        amenities: formData.amenities ? formData.amenities.split(",").map((s) => s.trim()).filter(s => s !== "") : [],
        images: formData.images.filter((img) => img.trim() !== ""),
      };

      await axios.put(`${API_URL}/accommodation/${id}`, payload);
      toast.success("Accommodation updated successfully!");
      setTimeout(() => navigate("/accommodation"), 1500);
    } catch (err) {
      console.error("Error updating accommodation:", err);
      toast.error(err.response?.data?.error || "Update failed.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <AdminDashboard>
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
          <Loader2 className="text-sky-500 animate-spin" size={48} />
          <p className="text-sky-900 font-black uppercase tracking-widest text-xs">Fetching Property Asset...</p>
        </div>
      </AdminDashboard>
    );
  }

  return (
    <AdminDashboard>
      <div className="font-sans">
        {/* Header Section */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-100 pb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <button
                onClick={() => navigate("/accommodation")}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-400 hover:text-sky-600"
              >
                <ArrowLeft size={18} />
              </button>
              <h1 className="text-2xl font-black text-sky-950 tracking-tight flex items-center gap-2">
                <Hotel className="text-sky-500" size={24} />
                Update Property
              </h1>
            </div>
            <p className="text-sm text-gray-400 font-bold ml-10">Manage and refine the details of your property listing.</p>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/accommodation")}
              className="text-[10px] font-black uppercase tracking-widest text-gray-400 hover:text-sky-600 transition-colors"
            >
              Cancel Edit
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">

          {/* Main Form Section */}
          <div className="lg:col-span-8">
            <form onSubmit={handleSubmit} className="space-y-10">

              {/* Section 1: Basic Information */}
              <div className="space-y-6">
                <div className="flex items-center gap-2 border-l-4 border-sky-500 pl-4 py-1">
                  <h3 className="text-xs font-black text-sky-950 uppercase tracking-widest">Basic Information</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Property Name</label>
                    <div className="relative">
                      <Hotel className="absolute left-4 top-1/2 -translate-y-1/2 text-sky-400" size={18} />
                      <input
                        name="accommodationName"
                        value={formData.accommodationName}
                        onChange={handleChange}
                        placeholder="e.g. Grand Heritage Villa"
                        required
                        className="w-full pl-12 pr-4 py-3.5 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-sky-500/5 focus:border-sky-400 transition-all font-bold text-sky-950 outline-none text-sm placeholder:text-gray-300"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Unique Code ID</label>
                    <div className="relative">
                      <Key className="absolute left-4 top-1/2 -translate-y-1/2 text-sky-400" size={18} />
                      <input
                        name="accommodationId"
                        value={formData.accommodationId}
                        onChange={handleChange}
                        placeholder="e.g. ACC-001"
                        required
                        className="w-full pl-12 pr-4 py-3.5 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-sky-500/5 focus:border-sky-400 transition-all font-bold text-sky-950 outline-none text-sm"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Property Type</label>
                    <div className="relative">
                      <List className="absolute left-4 top-1/2 -translate-y-1/2 text-sky-400" size={18} />
                      <select
                        name="type"
                        value={formData.type}
                        onChange={handleChange}
                        required
                        className="w-full pl-12 pr-4 py-3.5 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-sky-500/5 focus:border-sky-400 transition-all font-bold text-sky-950 outline-none appearance-none text-sm"
                      >
                        <option value="" disabled>Select Type</option>
                        <option value="Hotel">Hotel</option>
                        <option value="Villa">Villa</option>
                        <option value="Resort">Resort</option>
                        <option value="Apartment">Apartment</option>
                        <option value="Guest House">Guest House</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Contact Phone</label>
                    <div className="relative">
                      <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-sky-400" size={18} />
                      <input
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+94 77 XXX XXXX"
                        required
                        className="w-full pl-12 pr-4 py-3.5 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-sky-500/5 focus:border-sky-400 transition-all font-bold text-sky-950 outline-none text-sm"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Marketing Description</label>
                  <div className="relative">
                    <FileText className="absolute left-4 top-4 text-sky-400" size={18} />
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      placeholder="Describe the unique charm..."
                      required
                      rows="3"
                      className="w-full pl-12 pr-4 py-4 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-sky-500/5 focus:border-sky-400 transition-all font-bold text-sky-950 outline-none resize-none text-sm"
                    ></textarea>
                  </div>
                </div>
              </div>

              {/* Section 2: Location Details */}
              <div className="space-y-6">
                <div className="flex items-center gap-2 border-l-4 border-indigo-500 pl-4 py-1">
                  <h3 className="text-xs font-black text-sky-950 uppercase tracking-widest">Location & Address</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-2 md:col-span-1">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">City / Region</label>
                    <div className="relative">
                      <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-indigo-400" size={18} />
                      <input
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                        placeholder="e.g. Galle"
                        required
                        className="w-full pl-12 pr-4 py-3.5 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-indigo-500/5 focus:border-indigo-400 transition-all font-bold text-sky-950 outline-none text-sm"
                      />
                    </div>
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Full Physical Address</label>
                    <div className="relative">
                      <Navigation className="absolute left-4 top-1/2 -translate-y-1/2 text-indigo-400" size={18} />
                      <input
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        placeholder="Building No, Street Name, City"
                        required
                        className="w-full pl-12 pr-4 py-3.5 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-indigo-500/5 focus:border-indigo-400 transition-all font-bold text-sky-950 outline-none text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 3: Pricing & Policies */}
              <div className="space-y-6">
                <div className="flex items-center gap-2 border-l-4 border-emerald-500 pl-4 py-1">
                  <h3 className="text-xs font-black text-sky-950 uppercase tracking-widest">Pricing & Policies</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Price /Night</label>
                    <div className="relative">
                      <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 text-emerald-500" size={18} />
                      <input
                        type="number"
                        name="pricePerNight"
                        value={formData.pricePerNight}
                        onChange={handleChange}
                        placeholder="0.00"
                        required
                        className="w-full pl-12 pr-4 py-3.5 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-emerald-500/5 focus:border-emerald-400 transition-all font-bold text-sky-950 outline-none text-sm"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Currency</label>
                    <input
                      name="currency"
                      value={formData.currency}
                      onChange={handleChange}
                      placeholder="LKR"
                      required
                      className="w-full px-4 py-3.5 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white transition-all font-bold text-sky-950 outline-none text-sm"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Check-in</label>
                    <div className="relative">
                      <Clock className="absolute left-4 top-1/2 -translate-y-1/2 text-emerald-500" size={18} />
                      <input
                        name="checkInTime"
                        value={formData.checkInTime}
                        onChange={handleChange}
                        placeholder="14:00"
                        required
                        className="w-full pl-12 pr-4 py-3.5 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white transition-all font-bold text-sky-950 outline-none text-sm"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Check-out</label>
                    <div className="relative">
                      <Clock className="absolute left-4 top-1/2 -translate-y-1/2 text-emerald-500" size={18} />
                      <input
                        name="checkOutTime"
                        value={formData.checkOutTime}
                        onChange={handleChange}
                        placeholder="11:00"
                        required
                        className="w-full pl-12 pr-4 py-3.5 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white transition-all font-bold text-sky-950 outline-none text-sm"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-4 bg-emerald-50/50 p-4 rounded-xl border border-emerald-100 transition-all hover:bg-emerald-50">
                  <div className="relative flex items-center">
                    <input
                      type="checkbox"
                      name="availability"
                      checked={formData.availability}
                      onChange={handleChange}
                      className="w-5 h-5 rounded-lg border-emerald-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                    />
                  </div>
                  <div>
                    <p className="text-sm font-black text-emerald-900">Instant Online Availability</p>
                    <p className="text-[10px] text-emerald-700 font-bold">Manage whether the property is bookable in real-time.</p>
                  </div>
                </div>
              </div>

              {/* Section 4: Features & Images */}
              <div className="space-y-6">
                <div className="flex items-center gap-2 border-l-4 border-amber-500 pl-4 py-1">
                  <h3 className="text-xs font-black text-sky-950 uppercase tracking-widest">Features & Media</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Room Categories (Comma Sep)</label>
                    <input
                      name="roomType"
                      value={formData.roomType}
                      onChange={handleChange}
                      placeholder="e.g. Deluxe, Superior"
                      className="w-full px-4 py-3.5 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white transition-all font-bold text-sky-950 outline-none text-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Amenities (Comma Sep)</label>
                    <input
                      name="amenities"
                      value={formData.amenities}
                      onChange={handleChange}
                      placeholder="e.g. WiFi, Pool"
                      className="w-full px-4 py-3.5 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white transition-all font-bold text-sky-950 outline-none text-sm"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Property Gallery (Image URLs)</label>
                    <button
                      type="button"
                      onClick={addImageField}
                      className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-sky-600 hover:text-sky-700 transition-colors"
                    >
                      <Plus size={14} /> Add URL Field
                    </button>
                  </div>

                  <div className="space-y-3">
                    {formData.images.map((img, index) => (
                      <div key={index} className="flex gap-3 group">
                        <div className="relative flex-1">
                          <ImageIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-amber-400" size={16} />
                          <input
                            type="text"
                            placeholder={`Gallery Image #${index + 1} URL`}
                            value={img}
                            onChange={(e) => handleImageChange(index, e.target.value)}
                            className="w-full pl-11 pr-4 py-3 bg-gray-50/30 border border-gray-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-amber-500/5 focus:border-amber-400 transition-all font-bold text-sky-950 outline-none text-xs"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => removeImageField(index)}
                          disabled={formData.images.length === 1}
                          className="p-3 text-rose-300 hover:text-rose-500 hover:bg-rose-50 rounded-xl transition-all disabled:opacity-0"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="pt-6">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-5 bg-sky-600 text-white font-black rounded-2xl hover:bg-sky-700 active:scale-[0.98] transition-all shadow-xl shadow-sky-600/20 flex items-center justify-center gap-3 disabled:opacity-70 disabled:cursor-not-allowed uppercase tracking-widest text-sm"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={20} className="animate-spin" />
                      Saving Updates...
                    </>
                  ) : (
                    <>
                      Apply Modifications
                      <Save size={20} />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Preview Section */}
          <div className="lg:col-span-4 lg:sticky lg:top-8 self-start">
            <div className="flex items-center gap-2 mb-4 ml-2">
              <Eye size={16} className="text-sky-500" />
              <span className="text-[10px] font-black text-sky-950 uppercase tracking-widest">Live Asset Preview</span>
            </div>

            <div className="bg-gray-50 border border-gray-100 rounded-[2.5rem] overflow-hidden group border-dashed p-2">
              <div className="bg-white rounded-[2rem] overflow-hidden shadow-sm border border-gray-50">
                <div className="relative h-48 bg-gray-100 overflow-hidden">
                  {formData.images[0] ? (
                    <img
                      src={formData.images[0]}
                      alt="Preview"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000"
                      onError={(e) => {
                        e.target.src = "https://images.unsplash.com/photo-1566073771259-6a8506099945?q=80&w=1000&auto=format&fit=crop";
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-gray-300 gap-2">
                      <ImageIcon size={32} />
                      <p className="text-[9px] font-black uppercase tracking-widest">Gallery Preview</p>
                    </div>
                  )}
                  {formData.type && (
                    <div className="absolute top-4 left-4 px-3 py-1.5 bg-white/95 backdrop-blur rounded-full text-[8px] font-black uppercase tracking-widest text-sky-600 shadow-xl shadow-black/5">
                      {formData.type}
                    </div>
                  )}
                  <div className="absolute bottom-4 right-4 bg-emerald-500 text-white text-[8px] font-black px-2 py-1 rounded-md shadow-lg shadow-emerald-500/20">
                    EDITING MODE
                  </div>
                </div>

                <div className="p-6">
                  <div className="flex items-center gap-2 text-indigo-500 mb-2">
                    <MapPin size={10} />
                    <span className="text-[8px] font-black uppercase tracking-widest">
                      {formData.location || "Location TBD"}
                    </span>
                  </div>
                  <h2 className="text-lg font-black text-sky-950 mb-3 truncate leading-tight">
                    {formData.accommodationName || "Property Asset"}
                  </h2>

                  <div className="flex items-baseline gap-1 mb-4">
                    <span className="text-xs font-black text-emerald-600">{formData.currency || "LKR"}</span>
                    <span className="text-xl font-black text-sky-950">{formData.pricePerNight || "0"}</span>
                    <span className="text-[10px] text-gray-400 font-bold">/ night</span>
                  </div>

                  <p className="text-gray-400 text-[10px] font-bold leading-relaxed line-clamp-2 mb-6 h-10">
                    {formData.description || "Enter description to see it appear here..."}
                  </p>

                  <div className="pt-6 border-t border-gray-50 flex items-center justify-between">
                    <div className="flex -space-x-2">
                      {[1, 2, 3].map(i => (
                        <div key={i} className="w-5 h-5 rounded-full ring-2 ring-white bg-sky-100 flex items-center justify-center text-[6px] font-bold text-sky-600">
                          {String.fromCharCode(64 + i)}
                        </div>
                      ))}
                      <div className="w-5 h-5 rounded-full ring-2 ring-white bg-gray-50 flex items-center justify-center text-[6px] font-bold text-gray-300">+</div>
                    </div>
                    <div className="flex gap-1.5">
                      {formData.amenities.split(",").slice(0, 2).map((am, i) => am.trim() && (
                        <div key={i} className="px-2 py-1 bg-gray-50 rounded text-[7px] font-black text-gray-400 uppercase tracking-tighter">
                          {am.trim()}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 bg-amber-50 rounded-3xl p-6 border border-amber-100 shadow-sm relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-16 h-16 bg-amber-100/50 rounded-bl-[3rem] -mr-4 -mt-4 transition-all group-hover:scale-110"></div>
              <p className="text-[10px] font-bold text-amber-800 leading-relaxed italic relative z-10">
                "Keeping property information accurate and high-quality increases visibility and traveler trust!"
              </p>
            </div>
          </div>

        </div>
      </div>
    </AdminDashboard>
  );
}

export default UpdateAccommodation;
